/**
 * VideoEngineV2DemoPanel — dev-only UI for the Phase 2 stub demo.
 *
 * Renders an editable form (script, video prompt, title, duration,
 * founder/client toggle, billing upcharge, failure-injection knobs),
 * runs the v2 engine via useVideoEngineV2, and shows:
 *   - Per-step status (7 fixed steps)
 *   - Audio player for the stub voiceover
 *   - Final-video player for the stub MP4
 *   - Raw JSON result for debugging
 *
 * NOT linked from any nav. Reached via /v2-demo only, founder-only.
 *
 * Confirms in the UI itself:
 *   "Stub providers only — no ElevenLabs / Runway calls."
 *   "Business Brain is unchanged — script & prompt come from the caller."
 */
import React, { useEffect, useState } from 'react';
import { useVideoEngineV2 } from './useVideoEngineV2';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const SAMPLE_SCRIPT =
  'Stop overthinking content. Post one tip a day for thirty days. Save this and try it.';
const SAMPLE_PROMPT =
  'A real founder in their thirties in a soft sweater at a sunlit kitchen counter with a coffee mug, phone propped on a fruit bowl. Warm natural light, lived-in kitchen.';

const StepPill = ({ step, ok }) => {
  const label = step.replace(/_/g, ' ');
  const cls = ok
    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
    : 'bg-rose-500/15 border-rose-500/40 text-rose-200';
  return (
    <span className={`px-2 py-1 rounded text-xs font-mono border ${cls}`}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const absoluteUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND}${p || ''}`);

export default function VideoEngineV2DemoPage() {
  const [script, setScript] = useState(SAMPLE_SCRIPT);
  const [videoPrompt, setVideoPrompt] = useState(SAMPLE_PROMPT);
  const [title, setTitle] = useState('v2 demo run');
  const [duration, setDuration] = useState(12);
  const [simulateClient, setSimulateClient] = useState(false);
  const [upcharge, setUpcharge] = useState(false);

  const [forceVoiceFail, setForceVoiceFail] = useState(false);
  const [forceVideoFail, setForceVideoFail] = useState(false);
  const [forceMuxDropAudio, setForceMuxDropAudio] = useState(false);
  const [forceShortenEmpty, setForceShortenEmpty] = useState(false);

  // Phase 3 canary toggle — real ElevenLabs + Runway gen4.5 + ffmpeg.
  // Disabled in the UI unless the backend reports the env flag is ON.
  const [useRealProviders, setUseRealProviders] = useState(false);
  const [demoStatus, setDemoStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/v2-demo/status')
      .then((r) => { if (alive) setDemoStatus(r?.data || null); })
      .catch(() => { if (alive) setDemoStatus({ error: 'status unavailable' }); });
    return () => { alive = false; };
  }, []);

  const realProvidersAllowed = !!demoStatus?.real_providers_available;
  // Force OFF in UI state whenever the env flag is OFF so we can't POST
  // use_real_providers=true and trip the backend's 409 guard.
  useEffect(() => {
    if (!realProvidersAllowed && useRealProviders) {
      setUseRealProviders(false);
    }
  }, [realProvidersAllowed, useRealProviders]);

  const { status, result, error, run, reset } = useVideoEngineV2();
  const isRunning = status === 'running';

  const onRun = () => {
    if (useRealProviders) {
      // Hard confirm before burning ElevenLabs + Runway credits.
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'About to run REAL ElevenLabs + Runway gen4.5. This will consume credits. Continue?'
      );
      if (!ok) return;
    }
    run({
      script,
      video_prompt: videoPrompt,
      title,
      target_duration_sec: Number(duration) || 12,
      upcharge_confirmed: upcharge,
      simulate_client: simulateClient,
      use_real_providers: useRealProviders,
      force_voice_fail: forceVoiceFail,
      force_video_fail: forceVideoFail,
      force_mux_drop_audio: forceMuxDropAudio,
      force_shorten_empty: forceShortenEmpty,
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-8" data-testid="v2-demo-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* HEADER */}
        <header className="border-b border-slate-800 pb-4">
          <h1 className="text-2xl font-bold text-fuchsia-300" data-testid="v2-demo-title">
            Video Engine v2 — Phase 3 Demo
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Founder-only. Stubs by default; REAL providers (ElevenLabs +
            Runway gen4.5) unlock only when both the backend env flag and
            the per-run toggle are ON.
            <br />
            <span className="text-emerald-300">
              Business Brain is unchanged — niche, audience, offer, tone, and
              strategy are NOT computed here. v2 only renders the script &
              prompt you hand it.
            </span>
          </p>
        </header>

        {/* INPUT FORM */}
        <section className="bg-slate-900/50 border border-slate-800 rounded-lg p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
            Engine inputs
          </h2>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Title</label>
            <input
              data-testid="v2-input-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-800/80 border border-slate-700 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Voiceover script (finalized from Business Brain)
            </label>
            <textarea
              data-testid="v2-input-script"
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={3}
              className="w-full bg-slate-800/80 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
            />
            <div className="text-xs text-slate-500 mt-1">
              {script.trim().split(/\s+/).filter(Boolean).length} words
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Video prompt (finalized from Business Brain)
            </label>
            <textarea
              data-testid="v2-input-prompt"
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              rows={3}
              className="w-full bg-slate-800/80 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Target duration (sec)</label>
              <input
                data-testid="v2-input-duration"
                type="number"
                min={1}
                max={60}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-slate-800/80 border border-slate-700 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-4 pt-5">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="v2-input-simulate-client"
                  checked={simulateClient}
                  onChange={(e) => setSimulateClient(e.target.checked)}
                />
                Simulate client (not founder)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="v2-input-upcharge"
                  checked={upcharge}
                  onChange={(e) => setUpcharge(e.target.checked)}
                />
                Upcharge confirmed (+$2 over 15s)
              </label>
            </div>
          </div>
        </section>

        {/* FAILURE-INJECTION KNOBS */}
        <section className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-2">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Force failure mode (demo only)
          </h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ['v2-force-voice-fail', 'Voice provider fails', forceVoiceFail, setForceVoiceFail],
              ['v2-force-video-fail', 'Video provider fails', forceVideoFail, setForceVideoFail],
              ['v2-force-mux-drop-audio', 'Mux drops audio (final mp4 silent)', forceMuxDropAudio, setForceMuxDropAudio],
              ['v2-force-shorten-empty', 'Shorten returns empty', forceShortenEmpty, setForceShortenEmpty],
            ].map(([tid, label, val, set]) => (
              <label
                key={tid}
                className={`flex items-center gap-2 ${useRealProviders ? 'opacity-40' : ''}`}
              >
                <input
                  type="checkbox"
                  data-testid={tid}
                  checked={val}
                  onChange={(e) => set(e.target.checked)}
                  disabled={useRealProviders}
                />
                {label}
              </label>
            ))}
          </div>
          {useRealProviders && (
            <p className="text-[11px] text-amber-300 font-mono">
              Failure-injection knobs are ignored when real providers are enabled.
            </p>
          )}
        </section>

        {/* PROVIDER MODE — Phase 3 canary gate */}
        <section
          data-testid="v2-provider-mode-panel"
          className={`border rounded-lg p-5 space-y-2 ${
            useRealProviders
              ? 'bg-amber-500/10 border-amber-500/40'
              : 'bg-slate-900/30 border-slate-800'
          }`}
        >
          <h2 className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
            Provider mode
          </h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="v2-input-use-real-providers"
              checked={useRealProviders}
              onChange={(e) => setUseRealProviders(e.target.checked)}
              disabled={!realProvidersAllowed}
            />
            <span className={realProvidersAllowed ? 'text-amber-200' : 'text-slate-500'}>
              Use REAL providers (ElevenLabs + Runway gen4.5) — burns credits
            </span>
          </label>
          <div className="text-[11px] font-mono text-slate-400" data-testid="v2-status-detail">
            {!demoStatus && 'loading provider status…'}
            {demoStatus && (
              <>
                env_flag={String(demoStatus.env_real_providers_enabled)}
                {' · '}elevenlabs={String(demoStatus.elevenlabs_configured)}
                {' · '}runway={String(demoStatus.runway_configured)}
                {' · '}default_mode={demoStatus.default_provider_mode || '?'}
              </>
            )}
          </div>
          {!realProvidersAllowed && (
            <p className="text-[11px] text-slate-500 font-mono">
              Toggle is locked. Set <code>VIDEO_ENGINE_V2_REAL_PROVIDERS=1</code> in the
              backend env (and restart) to unlock real-provider canary runs.
            </p>
          )}
        </section>

        {/* ACTIONS */}
        <div className="flex items-center gap-3">
          <Button
            data-testid="v2-run-btn"
            onClick={onRun}
            disabled={isRunning || !script.trim() || !videoPrompt.trim()}
            className={
              useRealProviders
                ? 'bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold'
                : 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white'
            }
          >
            {isRunning
              ? 'Running v2 engine…'
              : useRealProviders
                ? 'Run v2 engine (REAL — burns credits)'
                : 'Run v2 engine (stubs)'}
          </Button>
          <Button
            data-testid="v2-reset-btn"
            onClick={reset}
            variant="ghost"
            disabled={isRunning}
          >
            Reset
          </Button>
          {status !== 'idle' && (
            <span
              className={`text-sm font-mono ${
                status === 'success' ? 'text-emerald-300' : status === 'failed' ? 'text-rose-300' : 'text-slate-400'
              }`}
              data-testid="v2-status-label"
            >
              status: {status}
            </span>
          )}
        </div>

        {/* RESULT */}
        {result && (
          <section className="space-y-4">
            <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-5 space-y-3" data-testid="v2-result-panel">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                Engine result — {result.ok ? 'OK' : 'FAILED'}
              </h2>
              {!result.ok && (
                <div className="text-rose-300 text-sm font-mono" data-testid="v2-error-message">
                  fail_reason: {result.fail_reason}
                  <br />
                  {result.error_message}
                </div>
              )}
              <div className="flex flex-wrap gap-2" data-testid="v2-step-pills">
                {(result.steps || []).map((s, i) => (
                  <StepPill key={i} step={s.step} ok={s.ok} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs font-mono text-slate-300">
                <div>final_script: <span className="text-slate-400">{result.final_script || '∅'}</span></div>
                <div>script_was_shortened: <span className="text-slate-400">{String(result.script_was_shortened)}</span></div>
                <div>final_audio_duration: <span className="text-slate-400">{result.final_audio_duration?.toFixed?.(2) ?? '∅'}s</span></div>
                <div>chosen_video_duration: <span className="text-slate-400">{result.chosen_video_duration ?? '∅'}s</span></div>
              </div>
            </div>

            {/* AUDIO PLAYBACK */}
            {result.audio_url && (
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Voiceover preview
                </h3>
                <audio
                  data-testid="v2-audio-player"
                  src={absoluteUrl(result.audio_url)}
                  controls
                  preload="metadata"
                  className="w-full"
                />
              </div>
            )}

            {/* FINAL VIDEO PLAYBACK */}
            {result.ok && result.final_video_url && (
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">
                  Final voiced video (stubbed)
                </h3>
                <video
                  data-testid="v2-final-video-player"
                  src={absoluteUrl(result.final_video_url)}
                  controls
                  preload="metadata"
                  className="w-full max-w-md rounded"
                />
              </div>
            )}

            {/* RAW JSON */}
            <details className="bg-slate-900/40 border border-slate-800 rounded-lg p-5">
              <summary className="text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer">
                Raw engine result JSON
              </summary>
              <pre
                data-testid="v2-raw-json"
                className="mt-3 text-xs font-mono text-slate-400 overflow-auto max-h-96"
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </section>
        )}

        {error && status === 'failed' && !result && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-lg p-4 text-sm font-mono" data-testid="v2-network-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
